import { useMemo } from 'react'

import { type SelectValue } from '@procurify/ui'
import { type FormikHelpers } from 'formik'
import * as Yup from 'yup'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from '../../messages'
import {
  type GlobalCommandOrgCategoriesFormSchema,
  type IGlobalCommandOrgCategoriesFormPrimaryCategory,
  type IGlobalCommandOrgCategoriesFormSecondaryCategory,
  type IGlobalCommandOrgCategoriesFormValue,
} from '../../types'

export const useGlobalCommandOrgCategoriesFormFormik = ({
  primaryCategoryLabel,
  secondaryCategoryLabel,
  initialValue,
  onSubmit,
}: {
  primaryCategoryLabel?: string
  secondaryCategoryLabel?: string
  initialValue?: IGlobalCommandOrgCategoriesFormValue
  onSubmit: (value: IGlobalCommandOrgCategoriesFormValue) => void
}) => {
  const intl = useProcurifyIntl()

  const initialValues = useMemo<GlobalCommandOrgCategoriesFormSchema>(
    () => ({
      primaryCategory: initialValue?.primaryCategory
        ? {
            label: initialValue.primaryCategory.name,
            value: initialValue.primaryCategory,
          }
        : null,
      secondaryCategory: initialValue?.secondaryCategory
        ? {
            label: initialValue.secondaryCategory.name,
            value: initialValue.secondaryCategory,
          }
        : null,
    }),
    [initialValue]
  )

  const validationSchema = useMemo(() => {
    const primaryRequired = primaryCategoryLabel
      ? intl.formatMessage(messages.agentLabelRequired, {
          label: primaryCategoryLabel,
        })
      : intl.formatMessage(messages.primaryCategoryRequired)
    const secondaryRequired = secondaryCategoryLabel
      ? intl.formatMessage(messages.agentLabelRequired, {
          label: secondaryCategoryLabel,
        })
      : intl.formatMessage(messages.secondaryCategoryRequired)

    return Yup.object<GlobalCommandOrgCategoriesFormSchema>({
      primaryCategory: Yup.object<
        SelectValue<IGlobalCommandOrgCategoriesFormPrimaryCategory>
      >()
        .nullable()
        .required(primaryRequired),
      // Only demanded when the picked primary category actually offers
      // secondary ones — otherwise the field is not on screen to answer.
      secondaryCategory: Yup.object<
        SelectValue<IGlobalCommandOrgCategoriesFormSecondaryCategory>
      >()
        .nullable()
        .when('primaryCategory', {
          is: (
            primaryCategory: Nullable<
              SelectValue<IGlobalCommandOrgCategoriesFormPrimaryCategory>
            >
          ) => (primaryCategory?.value.secondaryCategories.length ?? 0) > 0,
          then: Yup.object<
            SelectValue<IGlobalCommandOrgCategoriesFormSecondaryCategory>
          >()
            .nullable()
            .required(secondaryRequired),
          otherwise:
            Yup.object<
              SelectValue<IGlobalCommandOrgCategoriesFormSecondaryCategory>
            >().nullable(),
        }),
    })
  }, [intl, primaryCategoryLabel, secondaryCategoryLabel])

  const handleSubmit = (
    values: GlobalCommandOrgCategoriesFormSchema,
    { setSubmitting }: FormikHelpers<GlobalCommandOrgCategoriesFormSchema>
  ) => {
    onSubmit({
      primaryCategory: values.primaryCategory?.value ?? null,
      secondaryCategory: values.secondaryCategory?.value ?? null,
    })
    setSubmitting(false)
  }

  return { initialValues, validationSchema, handleSubmit }
}
